import { DeleteOutlined, MoreVert, PublishOutlined } from '@mui/icons-material';
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material';
import { useDeleteMenuMutation } from 'api/ordering-service/ordering-service';
import { TransformMenuResult } from 'api/ordering-service/ordering-service.types';
import AuthorizationGate from 'components/authorization-gate/authorization-gate.component';
import ConfirmationDrawer from 'components/drawers/confirmation-drawer/confirmation-drawer.component';
import { useUtilityDrawer } from 'components/drawers/utility-drawer/utility-drawer.hooks';
import { useMenuActions } from 'components/forms/menus-form/menus-form.hooks';
import {
  bindMenu,
  bindTrigger,
  usePopupState,
} from 'material-ui-popup-state/hooks';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';

type MenuPopoverProps = {
  data: TransformMenuResult;
};

const MenuPopover = ({ data }: MenuPopoverProps) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const deleteConfirmationDrawer = useUtilityDrawer<TransformMenuResult>();

  const [deleteMenu] = useDeleteMenuMutation();

  const menuPopupState = usePopupState({
    variant: 'popover',
    popupId: 'menu-popup',
  });
  const { handlePublishMenu } = useMenuActions(data);

  const onConfirmationValid = async () => {
    try {
      await deleteMenu({ catalogId: data?.catalogId }).unwrap();

      enqueueSnackbar(
        t('MenusDataGrid.column.actions.delete.alertMessage', {
          menuName: data?.title,
        }),
        { severity: 'success' },
      );
      deleteConfirmationDrawer.close();
    } catch (_e) {}
  };

  return (
    <>
      <AuthorizationGate any={['MENU_IMPORT_FOR_LOCATIONS', 'DELETE_MENU']}>
        <IconButton
          {...bindTrigger(menuPopupState)}
          data-testid="MenuInfo.IconButton"
          aria-label={t('forms.menusForm.menuCard.actions.aria-label')}
        >
          <MoreVert />
        </IconButton>
      </AuthorizationGate>
      <Menu {...bindMenu(menuPopupState)}>
        <AuthorizationGate permission="MENU_IMPORT_FOR_LOCATIONS">
          <MenuItem
            onClick={() => {
              menuPopupState.close();
              handlePublishMenu();
            }}
          >
            <ListItemIcon>
              <PublishOutlined />
            </ListItemIcon>
            <ListItemText
              primary={t('forms.menusForm.menuCard.actions.publish.label')}
            />
          </MenuItem>
        </AuthorizationGate>
        <AuthorizationGate permission="DELETE_MENU">
          <MenuItem
            onClick={() => {
              menuPopupState.close();
              deleteConfirmationDrawer.setOpen(true);
            }}
          >
            <ListItemIcon>
              <DeleteOutlined color="error" />
            </ListItemIcon>
            <ListItemText
              primary={t('forms.menusForm.menuCard.actions.delete.label')}
            />
          </MenuItem>
        </AuthorizationGate>
      </Menu>
      <ConfirmationDrawer
        open={deleteConfirmationDrawer.open}
        close={deleteConfirmationDrawer.close}
        subtitle={t('drawer.menuDeleteDrawer.subtitle', {
          menuTitle: data?.title,
        })}
        submitButtonLabel={t('drawer.menuDeleteDrawer.submitButtonLabel')}
        onValid={onConfirmationValid}
        cancel={deleteConfirmationDrawer.close}
        color="error"
      />
    </>
  );
};

export default MenuPopover;
